import type { TableProps } from 'antd'
import React, { useMemo, useRef, useState } from 'react'

import { LENS_LABEL } from 'features/catalog/constants'
import {
  Container,
  StyledTable,
} from 'features/catalog/features/table/catalog-table.styles'
import { CatalogTableSkeleton } from 'features/catalog/features/table/components/CatalogTableSkeleton/CatalogTableSkeleton'
import { CatalogTableToolbar } from 'features/catalog/features/table/components/CatalogTableToolbar/CatalogTableToolbar'
import { useCatalogTableColumns } from 'features/catalog/features/table/hooks/use-catalog-table-columns'
import { matchesFocusSearch } from 'features/catalog/features/table/search-matching'
import type { CatalogRowMetrics } from 'features/catalog/hooks/use-catalog-table-metrics'
import type { FocusListOption } from 'features/catalog/types'
import { buildProjectCatalogPathFromLens } from 'features/catalog/utils'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { usePageKeyboardShortcuts } from 'hooks/keyboard-shortcuts'
import type { UseSnapshotHistoryReturn } from 'hooks/use-snapshot-history'
import { useTableKeyboardNavigation } from 'hooks/use-table-keyboard-navigation'
import mixpanel from 'trackers/mixpanel'

const CATALOG_TABLE_PAGE_SIZE = 50

type Props = {
  projectId: string
  selectedLens: string
  onSelectLens: (lens: string) => void
  onSelectFocus: (value: string) => void
  /** A grouped theme row's expanded subtheme child was activated — routes
   * straight to that theme's detail page with the subtheme pre-selected,
   * instead of through `onSelectFocus` (which only names a top-level item). */
  onSelectSubtheme: (
    parentValue: string,
    subthemeValue: string,
    openNewTab?: boolean,
  ) => void
  panel: ChartPanel
  updatePanel: (panelId: string, updates: Partial<ChartPanel>) => void
  /** Row set + loading state for the current lens — owned by `Container`
   *  (via `useCatalogTableRows`) rather than fetched here, since Container
   *  also needs the unfiltered row set to resolve `selectedFocus` into a
   *  `FocusListOption` for the detail page. This table applies its own
   *  local search filter on top without reporting anything back up. */
  rows: FocusListOption[]
  isLoading: boolean
  metricsByValue: Record<string, CatalogRowMetrics>
  groupCountries: boolean
  onGroupCountriesChange: (value: boolean) => void
  groupThemes: boolean
  onGroupThemesChange: (value: boolean) => void
  /** Undo/redo over the filter sidebar and date range, owned by the page. */
  history: UseSnapshotHistoryReturn
}

function openInNewTab(
  projectId: string,
  selectedLens: string,
  value: string,
): void {
  window.open(
    buildProjectCatalogPathFromLens(projectId, selectedLens, value),
    '_blank',
    'noopener,noreferrer',
  )
}

/** A grouped theme's expanded subtheme row — carries both fields
 * `buildGroupedThemeRowsWithChildren` sets on every child, never on a
 * top-level (parent or ungrouped) row. */
function isSubthemeRow(
  row: FocusListOption,
): row is FocusListOption & { displayLabel: string; parentValue: string } {
  return row.displayLabel !== undefined && row.parentValue !== undefined
}

/**
 * Catalog's main-body list — a table of the current View's rows (products,
 * brands, sources, or themes), replacing the old card list that used to live
 * in the sidebar. Clicking a row navigates to the existing full-page
 * `/catalog/<collection>/<item>` detail route via `onSelectFocus`, unchanged
 * from before this refactor.
 */
const CatalogTable: React.FC<Props> = ({
  projectId,
  selectedLens,
  onSelectLens,
  onSelectFocus,
  onSelectSubtheme,
  panel,
  updatePanel,
  rows,
  isLoading,
  metricsByValue,
  groupCountries,
  onGroupCountriesChange,
  groupThemes,
  onGroupThemesChange,
  history,
}) => {
  const isGroupedThemes = selectedLens === 'theme' && groupThemes
  const [searchTerm, setSearchTerm] = useState('')

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows
    const searchLower = searchTerm.toLowerCase()
    return rows.filter((row) => matchesFocusSearch(row, searchLower))
  }, [rows, searchTerm])

  const navigateToRow = (row: FocusListOption): void => {
    mixpanel.track('catalog', { action: 'open item', value: row.value })
    onSelectFocus(row.value)
  }

  const {
    currentPage,
    pageSize,
    containerRef,
    onSearchKeyDown,
    onPageChange,
    onTableChange,
    getRowClassName,
  } = useTableKeyboardNavigation({
    rows: filteredRows,
    resetKey: `${selectedLens}_${searchTerm}`,
    pageSize: CATALOG_TABLE_PAGE_SIZE,
    onActivate: navigateToRow,
  })

  const searchInputRef = useRef<HTMLInputElement>(null)

  usePageKeyboardShortcuts(
    { '/': () => searchInputRef.current?.focus() },
    { respectInputs: true },
  )

  const columns = useCatalogTableColumns({
    selectedLens,
    groupCountries,
    metricsByValue,
    currentPage,
    pageSize,
  })

  const handleTableChange: NonNullable<
    TableProps<FocusListOption>['onChange']
  > = (...args) => {
    onTableChange(args[3].currentDataSource)
  }

  return (
    <Container ref={containerRef}>
      <CatalogTableToolbar
        selectedLens={selectedLens}
        onSelectLens={onSelectLens}
        panel={panel}
        updatePanel={updatePanel}
        groupCountries={groupCountries}
        onGroupCountriesChange={onGroupCountriesChange}
        groupThemes={groupThemes}
        onGroupThemesChange={onGroupThemesChange}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        onSearchKeyDown={onSearchKeyDown}
        searchInputRef={searchInputRef}
        history={history}
      />
      {isLoading ? (
        <CatalogTableSkeleton key="skeleton" columns={columns} />
      ) : (
        <StyledTable
          key="table"
          dataSource={filteredRows}
          columns={columns}
          rowKey="value"
          size="small"
          scroll={{ y: 1 }}
          pagination={{
            current: currentPage,
            pageSize,
            onChange: onPageChange,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} ${(
                LENS_LABEL[selectedLens] ?? 'items'
              ).toLowerCase()}`,
          }}
          onChange={handleTableChange}
          expandable={
            isGroupedThemes
              ? {
                  indentSize: 8,
                  defaultExpandAllRows: false,
                  // Column 0 is `#`, column 1 is Name — the expand chevron
                  // belongs on the theme's title, not the row number.
                  expandIconColumnIndex: 1,
                }
              : undefined
          }
          rowClassName={getRowClassName}
          onRow={(row) => ({
            role: 'link',
            tabIndex: 0,
            'aria-label': `Open ${row.displayLabel ?? row.value}`,
            style: { cursor: 'pointer' },
            onClick: (event) => {
              if (event.defaultPrevented) return
              if (isSubthemeRow(row)) {
                onSelectSubtheme(
                  row.parentValue,
                  row.displayLabel,
                  event.metaKey || event.ctrlKey,
                )
                return
              }
              if (event.metaKey || event.ctrlKey) {
                openInNewTab(projectId, selectedLens, row.value)
                return
              }
              navigateToRow(row)
            },
            onKeyDown: (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                if (isSubthemeRow(row)) {
                  onSelectSubtheme(row.parentValue, row.displayLabel)
                  return
                }
                navigateToRow(row)
              }
            },
            onMouseDown: (event) => {
              if (event.button === 1) {
                if (isSubthemeRow(row)) {
                  onSelectSubtheme(row.parentValue, row.displayLabel, true)
                  return
                }
                openInNewTab(projectId, selectedLens, row.value)
              }
            },
          })}
        />
      )}
    </Container>
  )
}

export default CatalogTable
